import React from 'react';
import { Mosaic, type MosaicNode } from 'react-mosaic-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Loader, Stack, Text } from '@mantine/core';

import { ControlBar } from './ControlBar';
import { NetworkMapSplitHandle } from './NetworkMapSplitHandle';
import { OuterParentClonotypeTable } from './OuterClonotypeTable/OuterParentClonotypeTable';
import { SequenceControlsBar } from './SequenceControlsBar';
import { NetworkMapClonotypesProvider } from '../../clonotype-network-map/NetworkMapClonotypesContext';
import { NetworkMapVisualization } from '../../clonotype-network-map/NetworkMapVisualization';
import { useNetworkMapClonotypes } from '../../clonotype-network-map/useNetworkMapClonotypes';
import type { AntibodyResponse } from '../../generated/generatedOpenAPI';
import { useEnsureSelectedClonotypeDefinition } from '../../hooks/useEnsureSelectedClonotypeDefinition';
import { selectChainType, selectFilterActive, selectSelectedSequences, setSelectedSequences } from '../../store/slices/antibodyAnalysis.slice';
import type { SelectedNode } from '../../store/slices/clonotypes.slice';
import { defaultGridExpanded, defaultGridSplitPercentage, setGridSplitPercentage, setIsGridExpanded } from '../../store/slices/clonotypes.slice';
import { ClonotypeDefinition } from '../../store/slices/preprocessing.slice';
import type { AppDispatch, AppState } from '../../store/store';
import { getClonotypeEnumFromDefinition } from '../../utils';
import { ChainWidthProvider } from '../context/ChainWidthContext';
import { useAntibodyTableData } from '../hooks/useAntibodyTableData';

type ViewId = 'grid' | 'table';

const s = {
  root: 'w-full h-full bg-background',
  page: 'flex flex-col h-full bg-background',
  splitWrapper: 'relative flex-1 overflow-hidden',
  tableWrapper: 'flex-1 overflow-hidden flex flex-col',
  tableInner: 'flex-1 min-h-0 overflow-hidden',
} as const;
const COLLAPSE_BELOW_PERCENTAGE = 4;

// Stable empty fallbacks for the `?? []` selector defaults below. Reusing one frozen array per
// type means the selector returns the SAME reference when nothing is selected, instead of a fresh
// `[]` each render (which react-redux would treat as a changed value and re-render every subscriber).
const EMPTY_BARCODES: string[] = [];
const EMPTY_CLONOTYPE_IDS: number[] = [];
const EMPTY_NODES: SelectedNode[] = [];

/**
 * AntibodyAnalysisPage
 *
 * Top-level page for the antibody sequencing dashboard.
 * Handles data fetching, loading/error states, and renders the
 * ControlBar + inline clonotype grid + OuterParentClonotypeTable.
 */
export function AntibodyAnalysisPage() {
  const { id: experimentId = '' } = useParams<{ id: string }>();

  const selectedClonotypeDefinition = useEnsureSelectedClonotypeDefinition();
  const clonotypeDefinitionEnum = getClonotypeEnumFromDefinition(selectedClonotypeDefinition ?? ClonotypeDefinition.HILARY);

  const selectedCells = useSelector((state: AppState) => state.clonotypes[experimentId]?.selectedCells ?? EMPTY_BARCODES);
  const selectedNodes = useSelector((state: AppState) => state.clonotypes[experimentId]?.selectedNodes ?? EMPTY_NODES);
  const selectedClonotypes = useSelector((state: AppState) => state.clonotypes[experimentId]?.selectedClonotypes ?? EMPTY_CLONOTYPE_IDS);
  const isGridExpanded = useSelector((state: AppState) => state.clonotypes[experimentId]?.isGridExpanded ?? defaultGridExpanded);
  const gridSplitPercentage = useSelector((state: AppState) => state.clonotypes[experimentId]?.gridSplitPercentage ?? defaultGridSplitPercentage);
  const chainType = useSelector(selectChainType);
  const selectedSequencesArray = useSelector(selectSelectedSequences);
  const filterActive = useSelector(selectFilterActive);

  // True while a bulk Select-all is preparing its expand-all render — drives the loader.
  const [isBulkSelecting, setIsBulkSelecting] = React.useState(false);

  // Latest export selection, read inside the sync effect below via a ref so the effect fires on
  // clonotype-selection changes (not on every individual sequence toggle) — avoids a feedback loop.
  const selectedSequencesRef = React.useRef(selectedSequencesArray);
  selectedSequencesRef.current = selectedSequencesArray;
  // The set of clonotype IDs whose sequences we've already auto-checked. Comparing the current
  // clonotype selection against this tells us which clonotypes JUST became selected (check their
  // sequences) vs JUST got deselected (uncheck theirs) — so we act only on those transitions and
  // never clobber the user's individual sequence un-checks in between.
  const clonotypesWithCheckedSequencesRef = React.useRef<Set<number>>(new Set());
  // Reset that tracking when the experiment changes: clonotype IDs are per-experiment (both have
  // clonotype 1, 2, …), so a stale entry would otherwise make us skip auto-checking a same-id
  // clonotype in the new experiment. Render-phase reset (React's "adjust state on prop change").
  const trackedExperimentRef = React.useRef(experimentId);
  if (trackedExperimentRef.current !== experimentId) {
    trackedExperimentRef.current = experimentId;
    clonotypesWithCheckedSequencesRef.current = new Set();
  }

  const dispatch = useDispatch<AppDispatch>();

  const { clonotypes, isLoading, error } = useAntibodyTableData({
    experimentId,
    barcodes: selectedCells,
    nodes: selectedNodes,
    clonotypeDefinition: clonotypeDefinitionEnum,
  });

  // Clear the bulk-select loader one frame after it's shown: it exists only to paint immediate
  // feedback before Select-all's expand-all render; once that frame passes the render is done (or
  // the `isLoading && no data yet` branch in tableContent takes over for a still-running fetch).
  // Gating only on isBulkSelecting means it always clears — it can't hang waiting on a fetch.
  React.useEffect(() => {
    if (!isBulkSelecting) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => setIsBulkSelecting(false));
    return () => cancelAnimationFrame(raf);
  }, [isBulkSelecting]);

  // Keep the sequence (export) selection in step with the clonotype (graph) selection: when a
  // clonotype becomes selected — in the network map, via a row checkbox, or Select all — check all
  // its sequences; when it's deselected, uncheck them. A clonotype's sequence IDs only exist once
  // its table row has loaded, so newly-selected clonotypes are handled as soon as their data arrives.
  React.useEffect(() => {
    const selectedIds = new Set(selectedClonotypes);
    const loadedClonotypesById = new Map(clonotypes.map((c) => [c.id, c]));
    const alreadyChecked = clonotypesWithCheckedSequencesRef.current;

    // Clonotypes that just became selected (and whose rows have loaded) → check their sequences.
    const clonotypesToCheck = selectedClonotypes.filter((id) => !alreadyChecked.has(id) && loadedClonotypesById.has(id));
    // Clonotypes we'd checked before but are no longer selected → uncheck their sequences.
    const clonotypesToUncheck = Array.from(alreadyChecked).filter((id) => !selectedIds.has(id));
    if (clonotypesToCheck.length === 0 && clonotypesToUncheck.length === 0) {
      return;
    }

    const clonotypeIdOfKey = (key: string) => Number(key.split('-')[0]); // key is `${clonotypeId}-${sequenceId}`
    const uncheckIds = new Set(clonotypesToUncheck);
    const nextSequenceKeys = new Set(selectedSequencesRef.current.filter((key) => !uncheckIds.has(clonotypeIdOfKey(key))));
    let changed = nextSequenceKeys.size !== selectedSequencesRef.current.length;
    for (const id of clonotypesToCheck) {
      for (const seq of loadedClonotypesById.get(id)!.sequences) {
        const key = `${id}-${seq.metadata.sequenceId}`;
        if (!nextSequenceKeys.has(key)) {
          nextSequenceKeys.add(key);
          changed = true;
        }
      }
    }

    clonotypesToCheck.forEach((id) => alreadyChecked.add(id));
    clonotypesToUncheck.forEach((id) => alreadyChecked.delete(id));
    if (changed) {
      dispatch(setSelectedSequences(Array.from(nextSequenceKeys)));
    }
  }, [selectedClonotypes, clonotypes, dispatch]);

  const splitPercentageOf = (node: MosaicNode<ViewId> | null): number | null =>
    node && typeof node === 'object' && 'splitPercentage' in node && typeof node.splitPercentage === 'number' ? node.splitPercentage : null;

  const handleToggleGrid = React.useCallback(() => {
    dispatch(setIsGridExpanded({ experimentId, isGridExpanded: !isGridExpanded }));
  }, [dispatch, experimentId, isGridExpanded]);

  // Single source of network-map clonotype data for the whole page. Shared via context to
  // ControlBar and NetworkMapVisualization so the transform runs once, not once per consumer.
  const networkMap = useNetworkMapClonotypes();
  const networkMapByClonotypeId = networkMap.byClonotypeId;

  const selectedSequences = React.useMemo(() => new Set(selectedSequencesArray), [selectedSequencesArray]);

  // Apply selected-sequence filter when active
  const filteredClonotypes = React.useMemo((): AntibodyResponse[] => {
    if (!filterActive || selectedSequences.size === 0) {
      return clonotypes;
    }
    return clonotypes
      .map((clonotype) => {
        const filteredSequences = clonotype.sequences.filter((seq) => selectedSequences.has(`${clonotype.id}-${seq.metadata.sequenceId}`));
        return filteredSequences.length > 0 ? { ...clonotype, sequences: filteredSequences } : null;
      })
      .filter((c): c is AntibodyResponse => c !== null);
  }, [clonotypes, filterActive, selectedSequences]);

  if (error) {
    const errorMessage =
      error && typeof error === 'object' && 'data' in error
        ? JSON.stringify((error as { data: unknown }).data)
        : 'An error occurred while fetching antibody data';
    return (
      <div className={s.root}>
        <Stack align="center" justify="center" className="h-full">
          <Text size="lg" fw={600} c="red">
            Error loading data
          </Text>
          <Text size="sm" c="dimmed">
            {errorMessage}
          </Text>
        </Stack>
      </div>
    );
  }

  const tableContent = (() => {
    if (isBulkSelecting || (isLoading && clonotypes.length === 0)) {
      return (
        <Stack align="center" justify="center" className="h-full">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading antibody analysis data...
          </Text>
        </Stack>
      );
    }
    if (selectedCells.length === 0) {
      return (
        <Stack align="center" justify="center" className="h-full">
          <Text size="lg" fw={600}>
            No cells selected
          </Text>
          <Text size="sm" c="dimmed">
            Select clonotypes from the network graph grid above to view antibody analysis
          </Text>
        </Stack>
      );
    }
    if (clonotypes.length === 0) {
      return (
        <Stack align="center" justify="center" className="h-full">
          <Text size="lg" fw={600}>
            No data available
          </Text>
          <Text size="sm" c="dimmed">
            No antibody data found for the selected cells
          </Text>
        </Stack>
      );
    }
    return (
      <ChainWidthProvider clonotypes={filteredClonotypes}>
        <OuterParentClonotypeTable
          clonotypes={filteredClonotypes}
          chainType={chainType}
          clonotypeMethod={selectedClonotypeDefinition ?? ClonotypeDefinition.HILARY}
          networkMapByClonotypeId={networkMapByClonotypeId}
        />
      </ChainWidthProvider>
    );
  })();

  return (
    <NetworkMapClonotypesProvider value={networkMap}>
      <div className={s.page}>
        <ControlBar
          clonotypes={clonotypes}
          isGridExpanded={isGridExpanded}
          onSelectAll={() => {
            setIsBulkSelecting(true);
            // Forget which clonotypes we've auto-checked so the sync effect re-checks every
            // selected clonotype's sequences — Select all means "all", even ones individually
            // unchecked earlier.
            clonotypesWithCheckedSequencesRef.current = new Set();
          }}
        />
        {isGridExpanded ? (
          <div className={s.splitWrapper}>
            <Mosaic<ViewId>
              renderTile={(id) => {
                if (id === 'grid') {
                  return <NetworkMapVisualization />;
                }
                return (
                  <div className={s.tableWrapper}>
                    <SequenceControlsBar />
                    <div className={s.tableInner}>{tableContent}</div>
                  </div>
                );
              }}
              value={{ direction: 'column', first: 'grid', second: 'table', splitPercentage: gridSplitPercentage }}
              onChange={(node: MosaicNode<ViewId> | null) => {
                const percentage = splitPercentageOf(node);
                if (percentage !== null && percentage >= COLLAPSE_BELOW_PERCENTAGE) {
                  dispatch(setGridSplitPercentage({ experimentId, splitPercentage: percentage }));
                }
              }}
              onRelease={(node: MosaicNode<ViewId> | null) => {
                const percentage = splitPercentageOf(node);
                if (percentage !== null && percentage < COLLAPSE_BELOW_PERCENTAGE) {
                  dispatch(setGridSplitPercentage({ experimentId, splitPercentage: defaultGridSplitPercentage }));
                  dispatch(setIsGridExpanded({ experimentId, isGridExpanded: false }));
                }
              }}
              resize={{ minimumPaneSizePercentage: 0 }}
              className="mosaic-no-style"
            />
            <NetworkMapSplitHandle isGridExpanded splitPercentage={gridSplitPercentage} onToggle={handleToggleGrid} />
          </div>
        ) : (
          <>
            <NetworkMapSplitHandle isGridExpanded={false} splitPercentage={0} onToggle={handleToggleGrid} />
            <div className={s.tableWrapper}>
              <SequenceControlsBar />
              <div className={s.tableInner}>{tableContent}</div>
            </div>
          </>
        )}
      </div>
    </NetworkMapClonotypesProvider>
  );
}
